import { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function PersonalInfoStep({ resume, saveResume, onNext, saving, registerPendingData }) {
  const [formData, setFormData] = useState({
    full_name: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: ''
  });
  const [targetRole, setTargetRole] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  // Initialize form from resume data
  useEffect(() => {
    if (resume?.personal_info) {
      console.log('[PersonalInfoStep] Loading from resume:', resume.personal_info);
      setFormData(resume.personal_info);
      setIsDirty(false);
    }
  }, [resume?.personal_info]);

  // Initialize target civilian role (resume-level field)
  useEffect(() => {
    setTargetRole(resume?.target_civilian_role || '');
  }, [resume?.target_civilian_role]);

  // Register pending data whenever form changes. Target civilian role is retired
  // from the UI but its stored value is preserved (re-sent unchanged) so existing
  // resumes are not overwritten.
  useEffect(() => {
    if (isDirty && registerPendingData) {
      registerPendingData({ personal_info: formData, target_civilian_role: targetRole });
    }
  }, [formData, targetRole, isDirty, registerPendingData]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
  }, []);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <div>
          <Label htmlFor="full_name" className="text-slate-900 font-medium">Full Name *</Label>
          <Input
            data-testid="personal-info-full-name-input"
            id="full_name"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            className="h-12 mt-2"
            placeholder="John Doe"
            required
          />
        </div>

        <div>
          <Label htmlFor="street" className="text-slate-900 font-medium">Street Address *</Label>
          <Input
            data-testid="personal-info-street-input"
            id="street"
            name="street"
            value={formData.street}
            onChange={handleChange}
            className="h-12 mt-2"
            placeholder="123 Main St"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="city" className="text-slate-900 font-medium">City *</Label>
            <Input
              data-testid="personal-info-city-input"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className="h-12 mt-2"
              placeholder="San Diego"
              required
            />
          </div>
          <div>
            <Label htmlFor="state" className="text-slate-900 font-medium">State *</Label>
            <Input
              data-testid="personal-info-state-input"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              className="h-12 mt-2"
              placeholder="CA"
              required
            />
          </div>
          <div>
            <Label htmlFor="zip_code" className="text-slate-900 font-medium">ZIP Code *</Label>
            <Input
              data-testid="personal-info-zip-input"
              id="zip_code"
              name="zip_code"
              value={formData.zip_code}
              onChange={handleChange}
              className="h-12 mt-2"
              placeholder="92101"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="phone" className="text-slate-900 font-medium">Phone Number *</Label>
            <Input
              data-testid="personal-info-phone-input"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="h-12 mt-2"
              placeholder="(555) 123-4567"
              required
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-slate-900 font-medium">Email Address *</Label>
            <Input
              data-testid="personal-info-email-input"
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="h-12 mt-2"
              placeholder="john.doe@example.com"
              required
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PersonalInfoStep;
